import _ from 'lodash';
import { useContext, useEffect } from 'react';
import { shouldShowField, validateField, getAccountActivityRuleErrors } from '../../../../../components/InputField/helper';
import TransactionTable from '../../../../../components/TransactionValues/TransactionTable';
import { DialogContext } from '../../../../../store/context/DialogContext';
import { useTranslation } from '../../../../../store/context/TranslationContext';
import ButtonGroupNew from '../../BusinessLiteForms/components/ButtonGroupNew';
import { getFirstOrderedMappedErrorMessage } from '../../helpers/validationOrder';
import { TRANSACTION_FIELDS } from '../../IPlushelper';
import { clearErrorsForFields, getNextStep, getVisibleWizardFieldOrder, WIZARD, WIZARD_NUM } from '../helper';
import { Button } from 'reactstrap';

const BPlusAccountActivityDeclaration = ({
    asLabel,
    hideButtons,
    data,
    handleUpdate,
    hasError,
    setHasError,
    loading,
    cyclosFields,
    errors,
    setErrors,
    iPlusFields,
    wizard,
    isForEDD,
    handleChange,
    handlePrev,
    editMode,
    applicationForm,
    actualTransactions = {},
    uiConfig = {
        showInputColumns: true
    },
    formType
}) => {
    const { showError } = useContext(DialogContext);
    const { t } = useTranslation();

    useEffect(() => {
        const initialErrors = {};
        iPlusFields.forEach((field) => {
            if (
                field.required &&
                field?.wizard === wizard &&
                shouldShowField(data, field.showIf)
            ) {
                const value = _.get(data, field.field);
                if (value === undefined || value === null || value === "") {
                    if (value !== false && !_.get(errors, field.field)) {
                        _.set(initialErrors, field.field, field.errorTypes?.required || field.error || "This field is required.");
                    }
                }
            }
        });

        const shownErrors = {};
        iPlusFields.forEach((field) => {
            if (field?.wizard === wizard && field.required && shouldShowField(data, field.showIf)) {
                const fieldValue = _.get(data, field.field);
                const error = validateField(field, fieldValue, data);
                if (error) {
                    _.set(shownErrors, field.field, error);
                }
            }
        });

        // setErrors({ ...shownErrors, ...initialErrors });
    }, [data, wizard, iPlusFields]);

    const checkNext = () => {
        const visibleFieldOrder = getVisibleWizardFieldOrder({
            fields: iPlusFields,
            wizard,
            data,
        });

        const firstErrorMessage = getFirstOrderedMappedErrorMessage(visibleFieldOrder, errors);
        if (firstErrorMessage) {
            setHasError(true);
            showError(firstErrorMessage);
            return false;
        }
        setErrors({});
        return true;
    };

    const handleSubmit = () => {
        const visibleFieldOrder = getVisibleWizardFieldOrder({
            fields: iPlusFields,
            wizard,
            data,
        });
        const crossFieldErrors = getAccountActivityRuleErrors(data, {
            depositLocationFields: ["Deposit_Locations"],
            paymentLocationFields: ["Payment_Locations"]
        });
        if (crossFieldErrors.length) {
            setHasError(true);
            const mapped = crossFieldErrors.reduce((acc, err) => {
                const fields = Array.isArray(err.activityFields) && err.activityFields.length ? [...err.activityFields, err.field] : [err.field];
                fields.filter(Boolean).forEach(field => _.set(acc, field, err.message));
                return acc;
            }, {});
            const mergedErrors = _.merge({}, errors, mapped);
            const firstErrorMessage = getFirstOrderedMappedErrorMessage(visibleFieldOrder, mergedErrors) || crossFieldErrors[0]?.message;
            showError(firstErrorMessage);
            setErrors((prev) => _.merge({}, prev, mapped));
            return;
        }
        const isFormFieldValid = checkNext();
        if (isFormFieldValid) {
            setHasError(false);
            return handleUpdate({ nextStep: getNextStep(wizard, applicationForm, data, editMode) });
        }
    };

    const handleUpdateFormData = (updatedData) => {
        handleChange(updatedData);
    };

    const handleErrors = (field, errorMsg) => {
        setErrors((prevErrors) => {
            const updatedErrors = _.cloneDeep(prevErrors);
            _.set(updatedErrors, field, errorMsg);
            return updatedErrors;
        })
    }

 
    const copyValue = (type) => {
        const clonedData = _.cloneDeep(data);
        if (type === 1) {
            /** 
             * If we copy Current expected activity then we clear the transaction fields from diffMap
             * and update the selfDeclaredValues in data state
             */
            // Remove transaction fields from diffMap
            clonedData.diffMap = _.omit(clonedData.diffMap, TRANSACTION_FIELDS);

            // Update transaction fields based on selfDeclaredValues
            TRANSACTION_FIELDS.forEach(field => {
                if (_.isNil(data.selfDeclaredValues?.[field])) {
                    delete clonedData[field];
                } else {
                    clonedData[field] = data.selfDeclaredValues[field];
                }
            });

            handleUpdateFormData(clonedData);
            clearErrorsForFields({
                setErrors,
                fields: TRANSACTION_FIELDS,
                setHasError,
            });
        } else if (type === 2) {
            /** 
             * If we copy actual activity then we update the transaction fields in diffMap
             * and update the actual_transactions in data state
             */
            // Create base diffMap with empty strings for nil selfDeclaredValues
            const emptyFieldsMap = _.fromPairs(
                TRANSACTION_FIELDS
                    .filter(field => _.isNil(data.selfDeclaredValues?.[field]))
                    .map(field => [field, ""])
            );
            // finding empty fields in actual_transaction
            const emptyFieldsMap2 = _.fromPairs(
                TRANSACTION_FIELDS
                    .filter(field => _.isNil(actualTransactions?.[field]))
                    .map(field => [field, ""])
            );

            // Merge existing diffMap, empty fields, and selfDeclaredValues
            const updatedDiffMap = _.merge(
                {},
                clonedData.diffMap,
                emptyFieldsMap,
                data.selfDeclaredValues
            );

            // Merge all data with actual_transactions and updated diffMap
            const updatedData = _.merge(
                {},
                clonedData,
                actualTransactions,
                emptyFieldsMap2,
                { diffMap: updatedDiffMap }
            );

            handleUpdateFormData(updatedData);
            clearErrorsForFields({
                setErrors,
                fields: TRANSACTION_FIELDS,
                setHasError,
            });
        }
    };


    return (
        <div className='row pt-3'>
            <span className='fs-5 fw-semibold mb-3 fst-italic'>{t("Note : Please review your business account expected activity versus your actual usage activity. If your needs are being met by the existing expected activity, then no change is required. If you need or anticipate increased activity in excess of the existing expected activity, please update your new expected activity.")}</span>

            {asLabel ? null : (
                <div className='d-flex flex-md-row flex-column align-items-end align-items-md-center justify-content-end gap-3 mb-3 mt-1'>
                    <Button
                        className='form-content-copy-button'
                        onClick={() => copyValue(1)}
                        disabled={loading}
                    >
                        {t("Copy Current Expected Activity")}
                    </Button>

                    <Button
                        className='form-content-copy-button'
                        onClick={() => copyValue(2)}
                        disabled={loading}
                    >
                        {t("Copy Actual Activity")}
                    </Button>
                </div>
            )}

            <TransactionTable
                asLabel={asLabel}
                config={{
                    fields: TRANSACTION_FIELDS,
                    cyclosFields
                }}
                data={data}
                hasError={hasError}
                handleErrors={handleErrors}
                isForEDD={isForEDD}
                handleUpdateFormData={handleUpdateFormData}
                showInputColumns={uiConfig.showInputColumns}
                actualTransactions={actualTransactions}
            />

            <div className='mt-4'>
                <ButtonGroupNew
                    onNextClick={handleSubmit}
                    onBackClick={() => { handlePrev() }}
                    step={WIZARD_NUM.BPlusAccountActivityDeclaration}
                    hideSubmitButton={hideButtons}
                    showPageNum={false}
                    pageNum={WIZARD_NUM.BPlusAccountActivityDeclaration}
                    asLabel={false}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default BPlusAccountActivityDeclaration;
